import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import useAPI from "../../../../../../api/hooks/useAPI";
import { inventoryBootstrapKeys } from "../../../../../../api/queryKeys";
import { Toast } from "../../../../../../components/Toast";
import { useInventoryBootstrap } from "../../../../../../contexts/InventoryBootstrapContext";
import { getInitialFilters } from "../../../filters/utils";

export default function useInventoryViews({
  appliedFilters,
  setAppliedFilters,
  entityFilters,
  setEntityFilters,
  entityFilterLogic,
  setEntityFilterLogic,
  setPostgresFilteredEntities,
  setIsSearching,
  handleToggleTableView,
  visibleColumns,
  setVisibleColumns,
}) {
  const queryClient = useQueryClient();
  const {
    trackEvent,
    createInventoryView,
    updateInventoryView,
    getEntitiesRds,
  } = useAPI();

  const [selectedViewId, setSelectedViewId] = useState("");
  const [isLoadingView, setIsLoadingView] = useState(false);
  const currentRequestRef = useRef(0);

  const { inventoryViews } = useInventoryBootstrap();

  const buildFiltersFromApplied = (appliedFilters) => {
    const filters = {};
    Object.keys(appliedFilters).forEach((key) => {
      if (appliedFilters[key] !== null && appliedFilters[key] !== "") {
        filters[key] = {
          field: key,
          value: appliedFilters[key],
        };
      }
    });
    return filters;
  };

  const saveViewMutation = useMutation({
    mutationFn: async (viewData) => {
      const filters = viewData.save_query_and_filters
        ? buildFiltersFromApplied(appliedFilters)
        : {};

      return createInventoryView({
        view_id: null,
        name: viewData.name,
        columns: visibleColumns,
        filters: filters,
        entity_filters: {
          filters: entityFilters || [],
          filterLogic: entityFilterLogic || [],
        },
        is_public: viewData.is_public,
      });
    },
    onSuccess: () => {
      Toast.success("View saved successfully");
      queryClient.invalidateQueries({ queryKey: inventoryBootstrapKeys.all });
      trackEvent("User saved inventory overview view");
    },
    onError: (error) => {
      Toast.error(
        error?.response?.data?.detail ||
          "Failed to save view. Please try again."
      );
    },
  });

  const updateViewMutation = useMutation({
    mutationFn: async ({ viewId, customerId, viewData, originalIsPublic }) => {
      const filters = viewData.save_query_and_filters
        ? buildFiltersFromApplied(appliedFilters)
        : {};
      return updateInventoryView({
        view_id: viewId,
        customer_id: customerId,
        name: viewData.name,
        columns: visibleColumns,
        filters: filters,
        entity_filters: {
          filters: entityFilters || [],
          filterLogic: entityFilterLogic || [],
        },
        is_public: originalIsPublic,
      });
    },
    onSuccess: () => {
      Toast.success("View updated successfully");
      queryClient.invalidateQueries({ queryKey: inventoryBootstrapKeys.all });
      trackEvent("User updated inventory overview view");
    },
    onError: (error) => {
      Toast.error(
        error?.response?.data?.detail ||
          "Failed to update view. Please try again."
      );
    },
  });

  const handleSaveView = async (viewData) => {
    await saveViewMutation.mutateAsync(viewData);
  };

  const handleUpdateView = async (
    viewId,
    customerId,
    viewData,
    originalIsPublic
  ) => {
    await updateViewMutation.mutateAsync({
      viewId,
      customerId,
      viewData,
      originalIsPublic,
    });
  };

  const handleViewSelect = async (viewId) => {
    if (!viewId) {
      currentRequestRef.current += 1;
      setSelectedViewId("");
      setIsLoadingView(false);

      if (setAppliedFilters) {
        setAppliedFilters(getInitialFilters());
      }

      if (setEntityFilters) {
        setEntityFilters([]);
      }

      if (setEntityFilterLogic) {
        setEntityFilterLogic([]);
      }

      if (setVisibleColumns) {
        setVisibleColumns(null);
      }

      if (setPostgresFilteredEntities) {
        setPostgresFilteredEntities(null);
      }

      if (setIsSearching) {
        setIsSearching(false);
      }

      return;
    }

    const selectedView = inventoryViews.find((view) => view.view_id === viewId);
    if (!selectedView) {
      Toast.error("View not found");
      return;
    }

    const requestId = ++currentRequestRef.current;
    setSelectedViewId(viewId);
    setIsLoadingView(true);

    try {
      if (setVisibleColumns) {
        setVisibleColumns(selectedView.selected_columns || null);
      }

      if (selectedView.selected_filters) {
        const restoredFilters = { ...getInitialFilters() };
        Object.keys(selectedView.selected_filters).forEach((key) => {
          const filter = selectedView.selected_filters[key];
          if (filter) {
            const filterValue =
              typeof filter === "object" && filter.value !== undefined
                ? filter.value
                : filter;
            if (filterValue !== null && filterValue !== "") {
              restoredFilters[key] = filterValue;
            }
          }
        });
        if (requestId === currentRequestRef.current) {
          setAppliedFilters(restoredFilters);
        }
      }

      if (selectedView.entity_filters && setEntityFilters) {
        const filters =
          typeof selectedView.entity_filters === "object" &&
          selectedView.entity_filters.filters
            ? selectedView.entity_filters.filters
            : Array.isArray(selectedView.entity_filters) // for the old views
            ? selectedView.entity_filters
            : [];

        const filterLogic =
          typeof selectedView.entity_filters === "object" &&
          selectedView.entity_filters.filterLogic
            ? selectedView.entity_filters.filterLogic
            : [];

        if (requestId === currentRequestRef.current) {
          setEntityFilters(filters);
        }

        if (setEntityFilterLogic && requestId === currentRequestRef.current) {
          setEntityFilterLogic(filterLogic);
        }

        if (filters.length > 0) {
          if (requestId === currentRequestRef.current) {
            handleToggleTableView();
          }
          if (setPostgresFilteredEntities && setIsSearching) {
            if (requestId === currentRequestRef.current) {
              setIsSearching(true);
            }
            try {
              const searchRequest = {
                filters: filters.map((f) => ({
                  field: f.field,
                  op: f.op,
                  value:
                    f.value === "true"
                      ? true
                      : f.value === "false"
                      ? false
                      : f.value,
                  target_table: f?.target_table,
                })),
                filter_logic: filterLogic,
                entity_type: null,
                page: 1,
                page_size: 10000,
              };

              let allItems = [];
              let currentPage = 1;
              let hasNext = true;

              while (hasNext) {
                const response = await getEntitiesRds({
                  ...searchRequest,
                  page: currentPage,
                  include_total: currentPage === 1,
                });

                if (requestId !== currentRequestRef.current) {
                  return;
                }

                const data = response.data || {};
                if (Array.isArray(data.entities)) {
                  allItems = [...allItems, ...data.entities];
                }

                hasNext = Boolean(data.pagination?.has_next);
                currentPage += 1;
              }

              if (requestId === currentRequestRef.current) {
                setPostgresFilteredEntities(allItems);
              }
            } catch (error) {
              if (requestId === currentRequestRef.current) {
                Toast.error(
                  error?.response?.data?.message ||
                    error?.message ||
                    "Failed to load entity filters. Please try again."
                );
              }
            } finally {
              if (requestId === currentRequestRef.current) {
                setIsSearching(false);
              }
            }
          }
        }
      }

      if (requestId === currentRequestRef.current) {
        Toast.success(`View "${selectedView.name}" loaded successfully`);
        trackEvent("User loaded inventory overview view", {
          view_name: selectedView.name,
        });
      }
    } catch (error) {
      if (requestId === currentRequestRef.current) {
        console.error("Error loading view:", error);
        Toast.error("Failed to load view. Please try again.");
      }
    } finally {
      if (requestId === currentRequestRef.current) {
        setIsLoadingView(false);
      }
    }
  };

  return {
    inventoryViews,
    selectedViewId,
    setSelectedViewId,
    handleViewSelect,
    handleSaveView,
    handleUpdateView,
    saveViewMutation,
    updateViewMutation,
    isLoadingView,
  };
}
